import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useState } from "react";
import { SetAlertType } from "schemas/functions";

import { Template } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";

interface MoveToFolderMenuProps {
  anchorEl: HTMLElement;
  template: Template;
  onClose: () => void;
  onMoved: () => void;
  setAlert: SetAlertType;
}

export default function MoveToFolderMenu({
  anchorEl,
  template,
  onClose,
  onMoved,
  setAlert,
}: MoveToFolderMenuProps) {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const { folders, fetchFolders } = useContext(OutreachTemplatesContext);

  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const move = async (folderId: number | null) => {
    if (!currentOrg?.id) {
      return;
    }
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/templates/${template.id}/folder`,
        "PUT",
        {},
        { folderId },
      );
      const folder = folders.find((f) => f.id === folderId);
      setAlert(
        folder
          ? `${template.name} added to ${folder.name}`
          : `${template.name} removed from folder`,
        "success",
      );
      onMoved();
      onClose();
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const createAndMove = async () => {
    if (!currentOrg?.id || !name.trim()) {
      return;
    }
    setSaving(true);
    try {
      const { folder } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/template-folders`,
        "POST",
        {},
        { name: name.trim() },
      );
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/templates/${template.id}/folder`,
        "PUT",
        {},
        { folderId: folder.id },
      );
      await fetchFolders();
      setAlert(`${template.name} added to ${folder.name}`, "success");
      onMoved();
      setNameDialogOpen(false);
      onClose();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={!nameDialogOpen}
        onClose={onClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {folders.map((folder) => (
          <MenuItem
            key={folder.id}
            selected={template.folderId === folder.id}
            onClick={() => move(folder.id)}
          >
            <ListItemIcon>
              <Box component="i" className="fa-regular fa-folder" />
            </ListItemIcon>
            <ListItemText>{folder.name}</ListItemText>
          </MenuItem>
        ))}
        {Boolean(template.folderId) && (
          <MenuItem onClick={() => move(null)}>
            <ListItemIcon>
              <Box component="i" className="fa-regular fa-folder-open" />
            </ListItemIcon>
            <ListItemText>Remove from folder</ListItemText>
          </MenuItem>
        )}
        {folders.length > 0 && <Divider />}
        <MenuItem
          onClick={() => {
            setName("");
            setNameDialogOpen(true);
          }}
        >
          <ListItemIcon>
            <Box component="i" className="fa-regular fa-plus" />
          </ListItemIcon>
          <ListItemText>New folder</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={nameDialogOpen} onClose={onClose}>
        <DialogTitle>New folder</DialogTitle>
        <DialogContent sx={{ minWidth: 360 }}>
          <TextField
            autoFocus
            fullWidth
            label="Folder name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") createAndMove();
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disableElevation
            disabled={saving || !name.trim()}
            onClick={createAndMove}
          >
            Create & move
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
